import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { FilmSlate, DownloadSimple, Spinner, Play, UploadSimple, X, CheckCircle, PencilSimple, Check, Plus, Trash, ArrowsClockwise } from "@phosphor-icons/react";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { pickBrollImages } from "@/lib/brollImages";
import { renderCampaignVideo, retryRenderVideo, fetchRenderStatus, downloadRenderedVideo, fetchRenderPreview, fetchRenderCostEstimate, fetchCaptionStyles, uploadCampaignClip, deleteCampaignClip, uploadCampaignFullVideo, deleteCampaignFullVideo, editVideoScript, regenerateHashtags } from "@/lib/api";
import { DASHBOARD } from "@/constants/testIds";
import { toast } from "sonner";

const VideoScriptCard = ({ script, testId, campaignId, voices = [], defaultVoiceId = "" }) => {
  const [renderStatus, setRenderStatus] = useState(null);
  const [renderStep, setRenderStep] = useState("");
  const [renderError, setRenderError] = useState("");
  const [lastRenderId, setLastRenderId] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [visualMode, setVisualMode] = useState("stock");
  const [costEstimate, setCostEstimate] = useState(null);
  const [captionStyles, setCaptionStyles] = useState([]);
  const [captionStyle, setCaptionStyle] = useState("bold_impact");
  const [customClipKeys, setCustomClipKeys] = useState(script?.custom_clip_keys || {});
  const [customFullVideoKey, setCustomFullVideoKey] = useState(script?.custom_full_video_key || null);
  const [uploadingSlot, setUploadingSlot] = useState(null);
  const [uploadingFullVideo, setUploadingFullVideo] = useState(false);
  const previewObjectUrlRef = useRef(null);
  const [selectedVoiceId, setSelectedVoiceId] = useState(defaultVoiceId);
  const [showRevoice, setShowRevoice] = useState(false);
  const [scriptOverride, setScriptOverride] = useState(null);
  const [editingScript, setEditingScript] = useState(false);
  const [editHook, setEditHook] = useState("");
  const [editVoiceover, setEditVoiceover] = useState("");
  const [editCaptions, setEditCaptions] = useState([]);
  const [editCta, setEditCta] = useState("");
  const [editHashtags, setEditHashtags] = useState("");
  const [savingScript, setSavingScript] = useState(false);
  const [regeneratingHashtags, setRegeneratingHashtags] = useState(false);
  const pollRef = useRef(null);

  useEffect(() => {
    setSelectedVoiceId(defaultVoiceId);
  }, [defaultVoiceId]);

  useEffect(() => {
    if (!campaignId) return;
    fetchRenderStatus(campaignId)
      .then((d) => {
        if (d.status !== "none") setRenderStatus(d.status);
        if (d.step) setRenderStep(d.step);
        if (d.render_id) setLastRenderId(d.render_id);
        maybeFetchPreview(d);
        if (d.status === "pending" || d.status === "processing") startPolling();
      })
      .catch(() => {});
    return () => {
      clearInterval(pollRef.current);
      if (previewObjectUrlRef.current) window.URL.revokeObjectURL(previewObjectUrlRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId]);

  useEffect(() => {
    fetchCaptionStyles().then(setCaptionStyles).catch(() => setCaptionStyles([]));
  }, []);

  useEffect(() => {
    if ((visualMode !== "ai_hook" && visualMode !== "ai_all") || !campaignId) {
      setCostEstimate(null);
      return;
    }
    const scope = visualMode === "ai_hook" ? "hook" : "all";
    fetchRenderCostEstimate(campaignId, scope).then(setCostEstimate).catch(() => setCostEstimate(null));
  }, [visualMode, campaignId]);

  const handleUploadClip = async (slot, file) => {
    if (!file) return;
    setUploadingSlot(slot);
    try {
      await uploadCampaignClip(campaignId, slot, file);
      setCustomClipKeys((prev) => ({ ...prev, [slot]: file.name }));
      toast.success(`Slot ${slot + 1} clip uploaded`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploadingSlot(null);
    }
  };

  const handleRemoveClip = async (slot) => {
    try {
      await deleteCampaignClip(campaignId, slot);
      setCustomClipKeys((prev) => {
        const next = { ...prev };
        delete next[slot];
        return next;
      });
    } catch {
      toast.error("Could not remove clip");
    }
  };

  const handleUploadFullVideo = async (file) => {
    if (!file) return;
    setUploadingFullVideo(true);
    try {
      await uploadCampaignFullVideo(campaignId, file);
      setCustomFullVideoKey(file.name);
      toast.success("Video uploaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploadingFullVideo(false);
    }
  };

  const handleRemoveFullVideo = async () => {
    try {
      await deleteCampaignFullVideo(campaignId);
      setCustomFullVideoKey(null);
    } catch {
      toast.error("Could not remove video");
    }
  };

  const maybeFetchPreview = (d) => {
    if (!d.preview_url || previewUrl) return;
    fetchRenderPreview(d.render_id)
      .then((blob) => {
        const url = window.URL.createObjectURL(blob);
        previewObjectUrlRef.current = url;
        setPreviewUrl(url);
      })
      .catch(() => {});
  };

  const startPolling = () => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const d = await fetchRenderStatus(campaignId);
        setRenderStatus((prevStatus) => {
          if (d.status === "done" && prevStatus !== "done") {
            toast.success("Your video finished rendering — it's ready to download.", {
              action: { label: "View", onClick: () => document.querySelector(`[data-testid="${DASHBOARD.renderVideoDownloadBtn(campaignId)}"]`)?.scrollIntoView({ behavior: "smooth" }) },
            });
          }
          return d.status;
        });
        setRenderStep(d.step || "");
        if (d.render_id) setLastRenderId(d.render_id);
        maybeFetchPreview(d);
        if (d.status === "done" || d.status === "failed") {
          clearInterval(pollRef.current);
          if (d.status === "failed") setRenderError(d.error || "Render failed.");
        }
      } catch {
        // keep polling silently
      }
    }, 3000);
  };

  const handleRender = async () => {
    setRenderError("");
    setRenderStep("");
    setShowRevoice(false);
    setPreviewUrl(null);
    setShowPreview(false);
    try {
      const aiVideoMode = visualMode === "ai_hook" || visualMode === "ai_all";
      const aiScope = visualMode === "ai_hook" ? "hook" : "all";
      const res = await renderCampaignVideo(campaignId, selectedVoiceId || undefined, aiVideoMode, aiScope, captionStyle);
      setLastRenderId(res.render_id);
      setRenderStatus("pending");
      startPolling();
    } catch (e) {
      setRenderError(e?.response?.data?.detail || "Could not start render.");
    }
  };

  const handleRetry = async () => {
    setRenderError("");
    if (!lastRenderId) return handleRender();
    try {
      await retryRenderVideo(lastRenderId);
      setRenderStatus("pending");
      setRenderStep("Resuming from last finished step");
      startPolling();
    } catch (e) {
      setRenderError(e?.response?.data?.detail || "Could not retry render.");
    }
  };

  const handlePreviewVoice = () => {
    const voice = voices.find((v) => v.voice_id === selectedVoiceId);
    if (voice?.preview_url) new Audio(voice.preview_url).play().catch(() => {});
  };

  const handleDownload = async () => {
    try {
      const statusData = await fetchRenderStatus(campaignId);
      const blob = await downloadRenderedVideo(statusData.render_id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `skilmark-video-${statusData.render_id}.mp4`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      setRenderError("Download failed. Try again.");
    }
  };

  if (!script) return null;
  const displayScript = scriptOverride || script;
  const isLive = script.broll_images && script.broll_images.length > 0;
  const images = isLive ? script.broll_images : pickBrollImages(script.broll_search_terms, 4);

  const startEditScript = () => {
    setEditHook(displayScript.hook_text || "");
    setEditVoiceover(displayScript.voiceover_script || "");
    setEditCaptions((displayScript.on_screen_captions || []).map((c) => ({ ...c })));
    setEditCta(displayScript.call_to_action || "");
    setEditHashtags((displayScript.hashtags || []).join(" "));
    setEditingScript(true);
  };

  const handleSaveScript = async () => {
    setSavingScript(true);
    try {
      const hashtags = editHashtags.split(/\s+/).filter(Boolean).map((h) => (h.startsWith("#") ? h : `#${h}`));
      const payload = { hook_text: editHook, voiceover_script: editVoiceover, on_screen_captions: editCaptions, call_to_action: editCta, hashtags };
      await editVideoScript(campaignId, payload);
      setScriptOverride({ ...displayScript, ...payload });
      setEditingScript(false);
      toast.success("Script updated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save changes");
    } finally {
      setSavingScript(false);
    }
  };

  const handleRegenerateHashtags = async () => {
    setRegeneratingHashtags(true);
    try {
      const hashtags = await regenerateHashtags(campaignId);
      setScriptOverride({ ...displayScript, hashtags });
      if (editingScript) setEditHashtags(hashtags.join(" "));
      toast.success("New hashtags generated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not regenerate hashtags");
    } finally {
      setRegeneratingHashtags(false);
    }
  };

  return (
    <div data-testid={testId} className="glass-panel rounded-3xl p-6 sm:p-10">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
        <div>
          <div className="flex items-center justify-between mb-4">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#7C93B8]">Hook (0-3s)</p>
            {campaignId && !editingScript && (
              <button
                type="button"
                data-testid={DASHBOARD.videoScriptEditBtn(campaignId)}
                onClick={startEditScript}
                className="text-white/40 hover:text-white flex items-center gap-1 text-[11px] uppercase tracking-wider"
              >
                <PencilSimple size={12} /> Edit
              </button>
            )}
          </div>

          {editingScript ? (
            <div className="space-y-4 mb-8">
              <textarea
                data-testid={DASHBOARD.videoScriptHookInput(campaignId)}
                value={editHook}
                onChange={(e) => setEditHook(e.target.value)}
                className="w-full bg-white/5 border border-white/15 rounded-xl px-3 py-2 text-white font-display text-lg font-bold"
                rows={2}
              />
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#7C93B8] mb-2">Voiceover Script</p>
                <textarea
                  value={editVoiceover}
                  onChange={(e) => setEditVoiceover(e.target.value)}
                  className="w-full bg-white/5 border border-white/15 rounded-xl px-3 py-2 text-sm text-white/80 min-h-[100px]"
                />
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-white/40 mb-2">On-Screen Captions</p>
                <div className="space-y-2">
                  {editCaptions.map((c, i) => (
                    <div key={i} className="flex gap-2 items-center">
                      <input
                        value={c.time || ""}
                        onChange={(e) => setEditCaptions((prev) => prev.map((p, idx) => (idx === i ? { ...p, time: e.target.value } : p)))}
                        className="w-16 bg-white/5 border border-white/15 rounded px-2 py-1 text-white font-mono text-xs"
                      />
                      <input
                        data-testid={DASHBOARD.videoScriptCaptionInput(campaignId, i)}
                        value={c.text || ""}
                        onChange={(e) => setEditCaptions((prev) => prev.map((p, idx) => (idx === i ? { ...p, text: e.target.value } : p)))}
                        className="flex-1 bg-white/5 border border-white/15 rounded px-2 py-1 text-white/80 text-sm"
                      />
                      <button type="button" onClick={() => setEditCaptions((prev) => prev.filter((_, idx) => idx !== i))} className="text-red-400/70 hover:text-red-400">
                        <Trash size={13} />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => setEditCaptions((prev) => [...prev, { time: "0:00", text: "" }])}
                    className="text-xs text-white/40 hover:text-white flex items-center gap-1"
                  >
                    <Plus size={12} /> Add caption line
                  </button>
                </div>
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-white/40 mb-2">Call to Action</p>
                <input
                  value={editCta}
                  onChange={(e) => setEditCta(e.target.value)}
                  className="w-full bg-white/5 border border-white/15 rounded-full px-4 py-2 text-white text-sm"
                />
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-white/40 mb-2">Hashtags</p>
                <div className="flex items-center gap-2">
                  <input
                    data-testid={DASHBOARD.videoScriptHashtagsInput(campaignId)}
                    value={editHashtags}
                    onChange={(e) => setEditHashtags(e.target.value)}
                    placeholder="#appmarketing #productivity #tech"
                    className="flex-1 bg-white/5 border border-white/15 rounded-full px-4 py-2 text-[#7DD3FC] text-sm"
                  />
                  <button
                    type="button"
                    data-testid={DASHBOARD.videoScriptRegenerateHashtagsBtn(campaignId)}
                    onClick={handleRegenerateHashtags}
                    disabled={regeneratingHashtags}
                    title="Regenerate hashtags"
                    className="text-white/50 hover:text-[#7DD3FC] p-2 rounded-full border border-white/15 disabled:opacity-30 shrink-0"
                  >
                    <ArrowsClockwise size={14} className={regeneratingHashtags ? "animate-spin" : ""} />
                  </button>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  data-testid={DASHBOARD.videoScriptSaveBtn(campaignId)}
                  onClick={handleSaveScript}
                  disabled={savingScript}
                  className="rounded-full bg-[#2563EB] text-white text-xs font-semibold px-4 py-2 flex items-center gap-1.5 disabled:opacity-50"
                >
                  {savingScript ? <Spinner size={13} className="animate-spin" /> : <Check size={13} />} Save
                </button>
                <button
                  type="button"
                  data-testid={DASHBOARD.videoScriptCancelBtn(campaignId)}
                  onClick={() => setEditingScript(false)}
                  disabled={savingScript}
                  className="rounded-full border border-white/15 text-white/60 text-xs font-semibold px-4 py-2"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <>
              <p className="font-display text-2xl sm:text-3xl font-bold mb-8 leading-tight">"{displayScript.hook_text}"</p>

              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#7C93B8] mb-3">Voiceover Script</p>
              <p className="text-sm text-white/70 leading-relaxed mb-8 whitespace-pre-line">{displayScript.voiceover_script}</p>

              <p className="text-xs font-bold uppercase tracking-[0.2em] text-white/40 mb-3">On-Screen Captions</p>
              <div className="space-y-2 mb-8">
                {(displayScript.on_screen_captions || []).map((c, i) => (
                  <div key={i} className="flex gap-3 text-sm">
                    <span className="text-white font-mono text-xs pt-0.5 shrink-0">{c.time}</span>
                    <span className="text-white/60">{c.text}</span>
                  </div>
                ))}
              </div>

              <div className="inline-flex items-center gap-2 rounded-full bg-[#2563EB] text-white font-semibold text-sm px-5 py-2.5 mb-4">
                {displayScript.call_to_action}
              </div>

              {(displayScript.hashtags || []).length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-white/40">Hashtags</p>
                    <button
                      type="button"
                      data-testid={DASHBOARD.videoScriptRegenerateHashtagsBtn(campaignId)}
                      onClick={handleRegenerateHashtags}
                      disabled={regeneratingHashtags}
                      title="Regenerate hashtags"
                      className="text-white/40 hover:text-[#7DD3FC] disabled:opacity-30"
                    >
                      <ArrowsClockwise size={13} className={regeneratingHashtags ? "animate-spin" : ""} />
                    </button>
                  </div>
                  <p className="text-sm text-[#7DD3FC] leading-relaxed">{displayScript.hashtags.join(" ")}</p>
                </div>
              )}
            </>
          )}

          {campaignId && (
            <div className="mt-6 pt-6 border-t border-white/10 space-y-3">
              {(!renderStatus || renderStatus === "none") && voices.length > 0 && (
                <div className="flex items-center gap-2">
                  <Select value={selectedVoiceId} onValueChange={setSelectedVoiceId}>
                    <SelectTrigger data-testid={DASHBOARD.renderVoiceSelect(campaignId)} className="bg-white/5 border-white/15 text-white h-9 text-xs flex-1">
                      <SelectValue placeholder="Narrator voice" />
                    </SelectTrigger>
                    <SelectContent>
                      {voices.map((v) => <SelectItem key={v.voice_id} value={v.voice_id}>{v.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <button
                    type="button"
                    data-testid={DASHBOARD.renderVoicePreviewBtn(campaignId)}
                    onClick={handlePreviewVoice}
                    disabled={!selectedVoiceId}
                    className="text-white/50 hover:text-white p-2 rounded-full border border-white/15 disabled:opacity-30 shrink-0"
                  >
                    <Play size={13} />
                  </button>
                </div>
              )}
              {(!renderStatus || renderStatus === "none") && (
                <div className="space-y-1.5">
                  <Select value={visualMode} onValueChange={setVisualMode}>
                    <SelectTrigger data-testid={DASHBOARD.renderAiVideoModeSelect(campaignId)} className="bg-white/5 border-white/15 text-white h-9 text-xs">
                      <SelectValue placeholder="Visual source" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="stock">Off — stock video only (no extra cost)</SelectItem>
                      <SelectItem value="ai_hook">AI Hook Only — fal.ai for the hook, stock for the rest</SelectItem>
                      <SelectItem value="ai_all">AI All Clips — fal.ai for every clip</SelectItem>
                      <SelectItem value="upload_clips">Upload My Own Clips — one per b-roll slot</SelectItem>
                      <SelectItem value="upload_full">Upload Full Video — skip clip generation entirely</SelectItem>
                    </SelectContent>
                  </Select>
                  {captionStyles.length > 0 && (
                    <Select value={captionStyle} onValueChange={setCaptionStyle}>
                      <SelectTrigger data-testid={DASHBOARD.renderCaptionStyleSelect(campaignId)} className="bg-white/5 border-white/15 text-white h-9 text-xs">
                        <SelectValue placeholder="Caption style" />
                      </SelectTrigger>
                      <SelectContent>
                        {captionStyles.map((s) => (
                          <SelectItem key={s.id} value={s.id}>Caption style: {s.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  {(visualMode === "ai_hook" || visualMode === "ai_all") && (
                    <p data-testid={DASHBOARD.renderCostEstimate(campaignId)} className="text-xs text-[#F97316]">
                      {costEstimate
                        ? `Estimated cost: ~$${costEstimate.estimated_cost_usd.toFixed(2)} for ${costEstimate.ai_clip_count} AI clip${costEstimate.ai_clip_count > 1 ? "s" : ""} (fal.ai), 1-3 min per clip.`
                        : "Estimating cost…"}
                    </p>
                  )}
                  {visualMode === "upload_clips" && (
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      {(script.broll_images || []).slice(0, 4).map((_, slot) => (
                        <div key={slot} className="flex flex-col items-center gap-1">
                          {customClipKeys[slot] ? (
                            <div className="flex items-center gap-1.5 text-xs text-emerald-400 bg-emerald-400/10 rounded-full px-2 py-1.5 w-full justify-center">
                              <CheckCircle size={13} weight="fill" /> Slot {slot + 1}
                              <button type="button" data-testid={DASHBOARD.uploadClipSlotRemoveBtn(slot)} onClick={() => handleRemoveClip(slot)} className="hover:text-white">
                                <X size={12} />
                              </button>
                            </div>
                          ) : (
                            <label className="flex items-center gap-1.5 text-xs text-white/50 border border-dashed border-white/20 rounded-full px-2 py-1.5 w-full justify-center cursor-pointer hover:border-white/40 hover:text-white transition-colors duration-300">
                              {uploadingSlot === slot ? <Spinner size={13} className="animate-spin" /> : <UploadSimple size={13} />}
                              Slot {slot + 1}
                              <input
                                type="file"
                                accept="video/*"
                                data-testid={DASHBOARD.uploadClipSlotInput(slot)}
                                className="hidden"
                                onChange={(e) => handleUploadClip(slot, e.target.files?.[0])}
                              />
                            </label>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  {visualMode === "upload_full" && (
                    <div>
                      {customFullVideoKey ? (
                        <div className="flex items-center gap-1.5 text-xs text-emerald-400 bg-emerald-400/10 rounded-full px-3 py-1.5 w-fit">
                          <CheckCircle size={13} weight="fill" /> Video uploaded
                          <button type="button" data-testid={DASHBOARD.uploadFullVideoRemoveBtn} onClick={handleRemoveFullVideo} className="hover:text-white">
                            <X size={12} />
                          </button>
                        </div>
                      ) : (
                        <label className="flex items-center gap-1.5 text-xs text-white/50 border border-dashed border-white/20 rounded-full px-3 py-1.5 w-fit cursor-pointer hover:border-white/40 hover:text-white transition-colors duration-300">
                          {uploadingFullVideo ? <Spinner size={13} className="animate-spin" /> : <UploadSimple size={13} />}
                          Upload video (max 100MB, up to 3 min)
                          <input
                            type="file"
                            accept="video/*"
                            data-testid={DASHBOARD.uploadFullVideoInput}
                            className="hidden"
                            onChange={(e) => handleUploadFullVideo(e.target.files?.[0])}
                          />
                        </label>
                      )}
                    </div>
                  )}
                </div>
              )}
              {(!renderStatus || renderStatus === "none") && (
                <button
                  data-testid={DASHBOARD.renderVideoBtn(campaignId)}
                  onClick={handleRender}
                  className="rounded-full border border-[#F97316] text-[#F97316] font-semibold text-sm px-5 py-2.5 hover:bg-[#F97316] hover:text-white transition-colors duration-300 flex items-center gap-2"
                >
                  <FilmSlate size={16} /> Render Downloadable MP4
                </button>
              )}
              {(renderStatus === "pending" || renderStatus === "processing") && (
                <div className="space-y-2">
                  <div data-testid={DASHBOARD.renderVideoProgress(campaignId)} className="flex items-center gap-2 text-sm text-white/50">
                    <Spinner size={16} className="animate-spin" /> {renderStep || "Starting up..."} <span className="text-white/30">— this renders in the background, safe to navigate away and check back later.</span>
                  </div>
                  {previewUrl && (
                    <div>
                      <button
                        type="button"
                        data-testid={DASHBOARD.renderVideoPreviewBtn(campaignId)}
                        onClick={() => setShowPreview((v) => !v)}
                        className="text-xs text-white/50 hover:text-white underline flex items-center gap-1.5"
                      >
                        <Play size={12} /> {showPreview ? "Hide" : "Preview"} rough cut (no voiceover/captions yet)
                      </button>
                      {showPreview && (
                        <video src={previewUrl} controls autoPlay muted className="mt-2 rounded-lg w-full max-w-[220px]" />
                      )}
                    </div>
                  )}
                </div>
              )}
              {renderStatus === "done" && (
                <div className="space-y-3">
                  <button
                    data-testid={DASHBOARD.renderVideoDownloadBtn(campaignId)}
                    onClick={handleDownload}
                    className="rounded-full bg-[#F97316] text-white font-semibold text-sm px-5 py-2.5 hover:bg-[#FB923C] transition-colors duration-300 flex items-center gap-2"
                  >
                    <DownloadSimple size={16} /> Download MP4
                  </button>

                  {voices.length > 0 && !showRevoice && (
                    <button
                      type="button"
                      data-testid={DASHBOARD.renderVideoChangeVoiceBtn(campaignId)}
                      onClick={() => setShowRevoice(true)}
                      className="text-xs text-white/40 hover:text-white underline block"
                    >
                      Change voice &amp; re-render
                    </button>
                  )}

                  {voices.length > 0 && showRevoice && (
                    <div className="flex items-center gap-2">
                      <Select value={selectedVoiceId} onValueChange={setSelectedVoiceId}>
                        <SelectTrigger data-testid={DASHBOARD.renderVoiceSelect(campaignId)} className="bg-white/5 border-white/15 text-white h-9 text-xs flex-1">
                          <SelectValue placeholder="Narrator voice" />
                        </SelectTrigger>
                        <SelectContent>
                          {voices.map((v) => <SelectItem key={v.voice_id} value={v.voice_id}>{v.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <button
                        type="button"
                        data-testid={DASHBOARD.renderVoicePreviewBtn(campaignId)}
                        onClick={handlePreviewVoice}
                        disabled={!selectedVoiceId}
                        className="text-white/50 hover:text-white p-2 rounded-full border border-white/15 disabled:opacity-30 shrink-0"
                      >
                        <Play size={13} />
                      </button>
                      <button
                        type="button"
                        data-testid={DASHBOARD.renderVideoRerenderBtn(campaignId)}
                        onClick={handleRender}
                        className="rounded-full border border-[#F97316] text-[#F97316] text-xs font-semibold px-4 py-2 hover:bg-[#F97316] hover:text-white transition-colors duration-300 shrink-0"
                      >
                        Re-render
                      </button>
                    </div>
                  )}
                </div>
              )}
              {renderStatus === "failed" && (
                <div data-testid={DASHBOARD.renderVideoError(campaignId)} className="text-sm text-red-400 flex items-center gap-2">
                  {renderError || "Render failed."}
                  <button data-testid={DASHBOARD.renderVideoRetryBtn(campaignId)} onClick={handleRetry} className="underline text-white/70 hover:text-white">Retry</button>
                </div>
              )}
              {renderError && renderStatus !== "failed" && <p className="text-xs text-red-400 mt-2">{renderError}</p>}
            </div>
          )}
        </div>

        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-white/40 mb-3 flex items-center gap-2">
            B-Roll {isLive && <span className="text-[10px] font-bold text-white normal-case tracking-normal">Live from Pexels</span>}
          </p>
          <div className="flex flex-wrap gap-2 mb-6">
            {(script.broll_search_terms || []).map((t, i) => (
              <span key={i} className="text-xs rounded-full border border-white/15 px-3 py-1.5 text-white/60">
                {t}
              </span>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-3">
            {images.map((src, i) => (
              <motion.div
                key={src}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className="aspect-[9/13] rounded-xl overflow-hidden border border-white/10"
              >
                <img
                  src={src}
                  alt="b-roll"
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    const fallback = pickBrollImages(script.broll_search_terms, 4)[i];
                    if (fallback && e.target.src !== fallback) e.target.src = fallback;
                  }}
                />
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoScriptCard;
